import React, { useEffect, useRef } from 'react';
/**
 * ShaderMesh — an animated WebGL MESH gradient inspired by Amplemarket's hero
 * shader.
 *
 * FLOW DIRECTION: The mesh is built around a SOURCE point (the prompt input,
 * at screen-center). Color visibly EMERGES from that source and streams
 * OUTWARD toward the edges — traveling radial waves plus outward-advected
 * noise — so it reads as color pouring out of the input. A light cream base
 * keeps things airy and lets the crisp funnel mask separate the plumes with
 * white.
 */
type RGB = [number, number, number];
const VERT = ['attribute vec2 aPosition;', 'void main() {', '  gl_Position = vec4(aPosition, 0.0, 1.0);', '}'].join('\n');
const FRAG = ['#ifdef GL_ES', 'precision highp float;', '#endif', '', 'uniform vec2 u_resolution;', 'uniform float u_time;', 'uniform vec2 u_source;', 'uniform vec3 u_col1;', 'uniform vec3 u_col2;', 'uniform vec3 u_col3;', '', 'float rand(vec2 co){', '  return fract(sin(dot(co.xy, vec2(12.9898,78.233))) * 43758.5453);', '}', '', 'void main(){', '  vec2 uv = gl_FragCoord.xy / u_resolution.xy;', '  float aspect = u_resolution.x / u_resolution.y;', '', '  // Work in aspect-corrected space so flow is symmetric left/right.', '  vec2 p = uv;      p.x *= aspect;', '  vec2 src = u_source; src.x *= aspect;', '', '  vec2 toEdge = p - src;', '  float dist = length(toEdge);', '  vec2 dir = toEdge / max(dist, 0.0001);', '', '  float t = u_time;', '', '  // STRAND STATE (held): a LARGE fixed radial offset is what collapses the', '  // mesh into thin radial strands. Instead of letting this grow unbounded', '  // with time (which kept drifting past the look we liked), we pin it at the', '  // magnitude the animation reached ~35s in, so the strand geometry stays', '  // put while the wave/phase terms below keep everything flowing.', '  vec2 flow = p - dir * 6.3;', '', '  // Traveling radial wave — bands emanating from the source outward.', '  float wave = sin(dist * 9.0 - t * 1.6);', '', '  // Overlapping streams, advected outward for the mesh mixing.', '  float n1 = sin(flow.x * 3.0 + wave) * cos(flow.y * 3.0 - t * 0.4);', '  float n2 = sin(flow.x * 4.2 - t * 0.5) * cos(flow.y * 4.0 + wave * 0.6);', '  float n3 = sin(flow.x * 2.4 + t * 0.3) * cos(flow.y * 2.6 - wave * 0.5);', '', '  vec3 c1 = u_col1 / 255.0; // cyan', '  vec3 c2 = u_col2 / 255.0; // violet', '  vec3 c3 = u_col3 / 255.0; // emerald', '  vec3 amber = vec3(1.0, 0.85, 0.60);', '  vec3 base = vec3(0.98, 0.99, 1.00); // very light cream base', '', '  vec3 col = base;', '  col = mix(col, c1, smoothstep(-0.8, 0.8, n1) * 0.80);', '  col = mix(col, c2, smoothstep(-0.7, 0.9, n2) * 0.80);', '  col = mix(col, c3, smoothstep(-0.7, 0.9, n3) * 0.75);', '  col = mix(col, amber, smoothstep(0.2, 1.0, n1 * n3) * 0.45);', '', '  // Fade toward the source so the strands get LIGHTER the closer they are', '  // to the middle, and only reach full saturation out near the edges.', '  float emerge = smoothstep(0.02, 0.60, dist);', '  col = mix(base, col, emerge);', '', '  // Subtle animated grain for texture.', '  float grain = rand(gl_FragCoord.xy + vec2(t * 60.0, t * 60.0));', '  col -= grain * 0.04;', '', '  gl_FragColor = vec4(col, 1.0);', '}'].join('\n');
function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.warn(gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}
export function ShaderMesh({
  col1 = [56, 189, 248],
  // cyan / sky-400
  col2 = [167, 139, 250],
  // violet-400
  col3 = [52, 211, 153],
  // emerald-400
  className,
  style
}: {
  col1?: RGB;
  col2?: RGB;
  col3?: RGB;
  className?: string;
  style?: React.CSSProperties;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const gl = canvas.getContext('webgl', {
      antialias: false,
      alpha: true,
      powerPreference: 'low-power'
    }) as WebGLRenderingContext | null || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
    if (!gl) {
      canvas.style.display = 'none';
      return;
    }
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) {
      canvas.style.display = 'none';
      return;
    }
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn(gl.getProgramInfoLog(prog));
      canvas.style.display = 'none';
      return;
    }
    const aPos = gl.getAttribLocation(prog, 'aPosition');
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const uRes = gl.getUniformLocation(prog, 'u_resolution');
    const uTime = gl.getUniformLocation(prog, 'u_time');
    const uSource = gl.getUniformLocation(prog, 'u_source');
    const uC1 = gl.getUniformLocation(prog, 'u_col1');
    const uC2 = gl.getUniformLocation(prog, 'u_col2');
    const uC3 = gl.getUniformLocation(prog, 'u_col3');
    const startT = Date.now();
    let W = 0;
    let H = 0;
    let raf: number | null = null;
    function resize() {
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      W = canvas.width;
      H = canvas.height;
    }
    function draw() {
      gl.viewport(0, 0, W, H);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(prog);
      gl.enableVertexAttribArray(aPos);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
      gl.uniform2fv(uRes, [W, H]);
      gl.uniform1f(uTime, 0.001 * (Date.now() - startT));
      // Source = center of the viewport, vertically aligned with the prompt
      // input (~56% down). Note WebGL's y origin is bottom-left, so we flip.
      gl.uniform2fv(uSource, [0.5, 1.0 - 0.56]);
      gl.uniform3fv(uC1, col1);
      gl.uniform3fv(uC2, col2);
      gl.uniform3fv(uC3, col3);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    function loop() {
      resize();
      draw();
      raf = requestAnimationFrame(loop);
    }
    function onVisibility() {
      if (document.hidden) {
        if (raf) {
          cancelAnimationFrame(raf);
          raf = null;
        }
      } else if (raf === null) {
        raf = requestAnimationFrame(loop);
      }
    }
    resize();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);
    raf = requestAnimationFrame(loop);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [col1, col2, col3]);
  return <canvas ref={canvasRef} className={className} style={style} aria-hidden="true" data-id="element-46" />;
}